<script lang="ts">
import type { RegisteredComponent } from '@builder.io/sdk-svelte';
import { Content, isPreviewing } from '@builder.io/sdk-svelte';
import { building } from '$app/environment';
import { page } from '$app/state';
import { CUSTOM_COMPONENTS } from '../../CustomComponents.js';
import { BUILDER_PUBLIC_API_KEY } from '../../config/apiKeys.js';
import { ERoutes } from '../../config/enums.js';
import { getShowcaseThumbnailMuxId } from '../../lib/utils/showcaseThumbnailVideo.js';
import {
    normalizeWorkCapabilityFilter,
    toWorkCapabilityOptions,
    workCapabilityMatches,
} from '../../lib/utils/workCapabilityFilters.js';
import CardGridItem from '../../stories/atoms/CardGridItem/CardGridItem.svelte';
import CardGrid from '../../stories/molecules/CardGrid/CardGrid.svelte';
import Filter from '../../stories/molecules/Filter/Filter.svelte';

const SHOWCASE_CARD_AUTOPLAY = true;
const SHOWCASE_AUTOPLAY_GROUP = 'showcase-thumbnail-autoplay';

interface FilterState {
    industry: string;
    services: string;
    [key: string]: string;
}

// biome-ignore lint/suspicious/noExplicitAny: Data type from +page.server.js
let { data }: { data: any } = $props();

let filterArray: FilterState = $state({ industry: '', services: '' });

$effect(() => {
    // During build/prerendering, searchParams throws an error when accessed
    if (building) return;

    try {
        const industryGetOption = page.url.searchParams.get('industry');
        const serviceGetOption = page.url.searchParams.get('services');

        filterArray.industry = industryGetOption || '';
        filterArray.services = normalizeWorkCapabilityFilter(serviceGetOption);
    } catch {
        // Silently fail during prerendering - filters will initialize empty
    }
});

const industryOptions = $derived(data.industry?.map((industry: { name: string }) => industry.name));

// shortcut to the actual content
let pageContent = $derived(data.builderProps.content?.data || {});

// we want to show unpublished content when in preview mode.
let canShowContent = $derived(pageContent || isPreviewing());

interface PostItem {
    id: string;
    data?: {
        industries?: { industry?: { value?: { name?: string } } }[];
        servicesProvided?: {
            service?: {
                id?: string;
                value?: { id?: string; name?: string };
            };
        }[];
        slug?: string;
        clientThumbnail?: string;
        showcaseThumbnailMuxId?: string;
        muxId?: string;
    };
    name?: string;
}

const serviceEntities = $derived.by(() => {
    const entities: { id: string; name: string }[] = [];

    for (const post of (data.allPosts as PostItem[] | undefined) ?? []) {
        for (const serviceItem of post.data?.servicesProvided ?? []) {
            const service = serviceItem.service;
            const id = service?.id || service?.value?.id;
            const name = service?.value?.name;

            if (
                typeof id === 'string' &&
                id.trim() &&
                typeof name === 'string' &&
                name.trim() &&
                !entities.some((entity) => entity.id === id)
            ) {
                entities.push({ id, name });
            }
        }
    }

    return entities;
});
const servicesOptions = $derived(toWorkCapabilityOptions(serviceEntities));

let listItems = $derived(
    (data.allPosts as PostItem[] | undefined)?.filter((item: PostItem) => {
        if (Reflect.ownKeys(filterArray).length === 0) {
            return true; // No filters active, include all items
        }

        // Check each active filter - item must match ALL filters (AND logic)
        for (let key in filterArray) {
            if (filterArray[key] !== '') {
                let matchesThisFilter = false;

                if (key === 'industry') {
                    item?.data?.industries?.forEach(
                        (industryItem: { industry?: { value?: { name?: string } } }) => {
                            if (
                                industryItem.industry?.value?.name?.toLowerCase() ===
                                filterArray[key]
                            ) {
                                matchesThisFilter = true;
                            }
                        },
                    );
                }

                if (key === 'services') {
                    item?.data?.servicesProvided?.forEach(
                        (serviceItem: {
                            service?: {
                                id?: string;
                                value?: { id?: string; name?: string };
                            };
                        }) => {
                            const service = serviceItem.service;

                            if (
                                workCapabilityMatches(
                                    service?.id || service?.value?.id,
                                    filterArray[key],
                                )
                            ) {
                                matchesThisFilter = true;
                            }
                        },
                    );
                }

                // If this filter didn't match, exclude the item (AND logic)
                if (!matchesThisFilter) {
                    return false;
                }
            }
        }

        // All filters matched
        return true;
    }),
);

// Check if any filters are active
let hasActiveFilters = $derived(
    Reflect.ownKeys(filterArray).length > 0 &&
        Object.values(filterArray).some((value) => value !== ''),
);

// Show all items with message if filters are active but no results
let showNoResultsMessage = $derived(hasActiveFilters && listItems?.length === 0);
let displayItems = $derived(showNoResultsMessage ? data.allPosts : listItems);
</script>

{#if canShowContent}
    <!-- force re-rendering of `Content` on client-side navigation -->
    {#key pageContent}
        <!-- Render builder content with all required props -->
        <div class="container container--id-{data?.builderProps?.content?.id}">
            <Content
                model="page"
                content={data.builderProps.content}
                apiKey={BUILDER_PUBLIC_API_KEY}
                customComponents={CUSTOM_COMPONENTS as RegisteredComponent[]}
            />
        </div>
        <div class="scroll-content-over-header">
            <Filter {industryOptions} {servicesOptions} bind:filterArray />

            {#if showNoResultsMessage}
                <div class="no-results-message">
                    <p>Those specific filters don't match any project yet, but here are projects you may be interested in</p>
                </div>
            {/if}

            <CardGrid
                noAlternatingColumns={true}
                itemsPerRow={3}
                hasBorder={false}
                hasBottomMargin={false}
            >
                {#snippet children()}
                    {#each displayItems ?? [] as post, index (post.id)}
                        <CardGridItem
                            class="builder-block"
                            to="{ERoutes.Work}{post.data?.slug ?? ''}"
                            title={post.name}
                            image={{
                                src: post.data?.clientThumbnail,
                                altText: post.name,
                            }}
                            muxId={getShowcaseThumbnailMuxId(post)}
                            autoplayVideo={SHOWCASE_CARD_AUTOPLAY}
                            autoplayGroup={SHOWCASE_AUTOPLAY_GROUP}
                            autoplayOwnerId={post.id}
                            autoplayPriority={index}
                            enableVideoHoverZoom={true}
                        />
                    {/each}
                {/snippet}
            </CardGrid>
        </div>
    {/key}
{:else}
    Content Not Found
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/mq-config';
    @use 'sass-mq/mq' as *;

    .scroll-content-over-header {
        background: var(--colour-white);
        position: relative;
        z-index: 1;
    }

    .no-results-message {
        padding: var(--layout-pagegrid-margin);
        text-align: center;

        p {
            color: var(--colour-text-secondary, #666);
            font-size: 1.125rem;
            margin: 0;
        }
    }

    .container
        > :global(
            div
                > .builder-blocks
                > .builder-block:not(> .section-with-background)
        ) {
        padding-bottom: var(--layout-pagegrid-margin);
        padding-left: var(--layout-pagegrid-margin);
        padding-right: var(--layout-pagegrid-margin);
        padding-top: var(--layout-pagegrid-padding-top);
    }
</style>
